import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Currency, CurrencyStatus, CurrencyStatusLabels } from '../../../core/models/currency';
import { CurrencyService } from '../../../core/services/currency.service';
import { CurrencyOrderService } from '../../../core/services/currency-order.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TrackedDialogService } from '../../../core/services/tracked-dialog.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CurrencyFormComponent } from './currency-form/currency-form.component';
import { TooltipModule } from 'primeng/tooltip';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { DropdownModule } from 'primeng/dropdown';
import { EmPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permission } from '../../../core/enums/permission.enum';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'em-currency',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    ToastModule,
    ConfirmDialogModule,
    TagModule,
    DropdownModule,
    TranslatePipe,
    EmPermissionDirective,
    TooltipModule
  ],
  templateUrl: './currency.component.html',
  styleUrls: ['./currency.component.scss'],
  providers: [MessageService, ConfirmationService, TrackedDialogService]
})
export class CurrencyComponent implements OnInit {
  currencies: Currency[] = [];
  activeCurrencies: Currency[] = [];
  selectedCenterRateCurrencyId: number | null = null;
  isLoading = false;
  centerRateCurrencyLoading = false;
  centerRateCurrencySaving = false;
  ref: DynamicDialogRef | undefined;
  translations: any = {};
  Permission = Permission;

  CurrencyStatus = CurrencyStatus;
  readonly statusLabels = CurrencyStatusLabels;
  orderChanged = false;
  dragIndex: number | null = null;
  dragOverIndex: number | null = null;
  constructor(
    private currencyService: CurrencyService,
    private currencyOrderService: CurrencyOrderService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.loadCurrencies();
    this.loadCenterRateCurrencyOptions();
  }

  loadTranslations(): void {
    this.translate.get([
      'layout.configurations.currency.title',
      'layout.configurations.currency.add',
      'layout.configurations.currency.edit',
      'layout.configurations.currency.delete.title',
      'layout.configurations.currency.delete.message',
      'layout.configurations.currency.delete.yes',
      'layout.configurations.currency.delete.no',
      'layout.configurations.currency.delete.success.title',
      'layout.configurations.currency.delete.success.detail',
      'layout.configurations.currency.delete.error.title',
      'layout.configurations.currency.delete.error.detail',
      'layout.configurations.currency.delete.cancel.title',
      'layout.configurations.currency.delete.cancel.detail',
      'layout.configurations.currency.addCurrency.title',
      'layout.configurations.currency.addCurrency.cancelMessage.title',
      'layout.configurations.currency.addCurrency.cancelMessage.detail',
      'layout.configurations.currency.addCurrency.successMessage.title',
      'layout.configurations.currency.addCurrency.successMessage.detail',
      'layout.configurations.currency.editCurrency.title',
      'layout.configurations.currency.edit.cancelMessage.title',
      'layout.configurations.currency.edit.cancelMessage.detail',
      'layout.configurations.currency.edit.successMessage.title',
      'layout.configurations.currency.edit.successMessage.detail',
      'layout.configurations.currency.saveOrder',
      'layout.configurations.currency.resetOrder',
      'layout.configurations.currency.dragHint',
      'layout.configurations.currency.centerRateCurrency.label',
      'layout.configurations.currency.centerRateCurrency.placeholder',
      'layout.configurations.currency.centerRateCurrency.savedTitle',
      'layout.configurations.currency.centerRateCurrency.savedDetail',
      'layout.configurations.currency.centerRateCurrency.loadError',
      'layout.configurations.currency.centerRateCurrency.saveError'
    ]).subscribe((translations) => {
      this.translations = translations;
    });
  }

  loadCenterRateCurrencyOptions(): void {
    this.centerRateCurrencyLoading = true;

    forkJoin({
      activeCurrencies: this.currencyService.getActiveCurrencies(),
      centerRateCurrency: this.currencyService.getCenterRateCurrency()
    }).subscribe({
      next: ({ activeCurrencies, centerRateCurrency }) => {
        this.activeCurrencies = activeCurrencies;
        this.selectedCenterRateCurrencyId = centerRateCurrency?.currencyId ?? null;
        this.centerRateCurrencyLoading = false;
      },
      error: (error) => {
        console.error('Error loading center rate currency:', error);
        this.centerRateCurrencyLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('layout.configurations.currency.save.error.title'),
          detail: this.translate.instant('layout.configurations.currency.centerRateCurrency.loadError'),
          life: 3000
        });
      }
    });
  }

  onCenterRateCurrencyChange(currencyId: number | null): void {
    if (!currencyId) {
      return;
    }

    this.centerRateCurrencySaving = true;

    this.currencyService.updateCenterRateCurrency(currencyId).subscribe({
      next: (centerRateCurrency) => {
        this.selectedCenterRateCurrencyId = centerRateCurrency.currencyId;
        this.centerRateCurrencySaving = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('layout.configurations.currency.centerRateCurrency.savedTitle'),
          detail: this.translate.instant('layout.configurations.currency.centerRateCurrency.savedDetail'),
          life: 3000
        });
      },
      error: (error) => {
        console.error('Error saving center rate currency:', error);
        this.centerRateCurrencySaving = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('layout.configurations.currency.save.error.title'),
          detail: this.translate.instant('layout.configurations.currency.centerRateCurrency.saveError'),
          life: 3000
        });
        this.loadCenterRateCurrencyOptions();
      }
    });
  }

  loadCurrencies(): void {
    this.isLoading = true;

    this.currencyService.getAll().subscribe({
      next: (currencies) => {
       const savedOrder = this.currencyOrderService.getOrder();
        if (savedOrder.length > 0) {
          const nameToObj = new Map(currencies.map(c => [c.currencyName.toUpperCase(), c]));
          const ordered: Currency[] = [];
          savedOrder.forEach((name: string) => {
            const c = nameToObj.get(name.toUpperCase());
            if (c) ordered.push(c);
          });
          currencies.forEach(c => {
            if (!savedOrder.includes(c.currencyName.toUpperCase())) {
              ordered.push(c);
            }
          });
          this.currencies = ordered;
        } else {
          this.currencies = currencies;
        }
        this.orderChanged = false;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading currencies:', error);
        console.error('Status:', error.status);
        console.error('Message:', error.message);
        
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.toast.error'),
          detail: this.translate.instant('layout.configurations.currency.loadError', { status: error.status, statusText: error.statusText })
        });
        this.isLoading = false;
      }
    });
  }
 onDragStart(index: number): void {
    this.dragIndex = index;
    this.dragOverIndex = index;
  }
  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }
  onDragEnter(index: number): void {
    this.dragOverIndex = index;
  }
  onDrop(event: DragEvent): void {
    event.preventDefault();
    const toIndex = this.dragOverIndex;
    if (this.dragIndex === null || toIndex === null || this.dragIndex === toIndex) {
      this.dragIndex = null;
      this.dragOverIndex = null;
      return;
    }
    const items = [...this.currencies];
    const fromIndex = this.dragIndex;
    const [moved] = items.splice(fromIndex, 1);
    items.splice(toIndex, 0, moved);
    this.currencies = items;
    this.dragIndex = null;
    this.dragOverIndex = null;
    this.orderChanged = true;
  }
  onDragEnd(): void {
    this.dragIndex = null;
    this.dragOverIndex = null;
  }
  // ─── Save / Reset Order ──────────────────────────────────────────────────────
  saveOrder(): void {
    const order = this.currencies.map(c => c.currencyName.toUpperCase());
    this.currencyOrderService.saveOrder(order);
    this.orderChanged = false;
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('layout.configurations.currency.orderSaved.title'),
      detail: this.translate.instant('layout.configurations.currency.orderSaved.detail'),
      life: 3000
    });
  }
  resetOrder(): void {
    this.currencyOrderService.clearOrder();
    this.orderChanged = false;
    this.loadCurrencies();
    this.messageService.add({
      severity: 'info',
      summary: this.translate.instant('layout.configurations.currency.orderReset.title'),
      detail: this.translate.instant('layout.configurations.currency.orderReset.detail'),
      life: 3000
    });
  }
  onAddCurrency(): void {
    this.ref = this.dialogService.open(CurrencyFormComponent, {
      header: this.translations['layout.configurations.currency.addCurrency.title'] || 'Add Currency',
      width: '600px',
      data: { mode: 'add' }
    });

    this.ref.onClose.subscribe((result) => {
      if (!result) {
        this.messageService.add({
          severity: 'info',
          summary: this.translations['layout.configurations.currency.addCurrency.cancelMessage.title'],
          detail: this.translations['layout.configurations.currency.addCurrency.cancelMessage.detail'],
          life: 3000
        });
        return;
      }

      if (result.type === 'success') {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('layout.configurations.currency.addCurrency.successMessage.title'),
          detail: this.translate.instant('layout.configurations.currency.addCurrency.successMessage.detail'),
          life: 4000
        });
        this.loadCurrencies();
        this.loadCenterRateCurrencyOptions();
      }
    });
  }

  onEditCurrency(currency: Currency): void {
    this.ref = this.dialogService.open(CurrencyFormComponent, {
      header: this.translate.instant('layout.configurations.currency.editCurrency.title') || 'Edit Currency',
      width: '600px',
      data: { mode: 'edit', ...currency }
    });

    this.ref.onClose.subscribe((result) => {
      if (!result) {
        this.messageService.add({
          severity: 'info',
          summary: this.translate.instant('layout.configurations.currency.edit.cancelMessage.title'),
          detail: this.translate.instant('layout.configurations.currency.edit.cancelMessage.detail'),
          life: 3000
        });
        return;
      }

      if (result.type === 'success') {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('layout.configurations.currency.edit.successMessage.title'),
          detail: this.translate.instant('layout.configurations.currency.edit.successMessage.detail'),
          life: 4000
        });
        this.loadCurrencies();
        this.loadCenterRateCurrencyOptions();
      }
    });
  }

  onDeleteCurrency(event: Event, currency: Currency): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.translate.instant('layout.configurations.currency.delete.message'),
      header: this.translate.instant('layout.configurations.currency.delete.title'),
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: this.translate.instant('layout.configurations.currency.delete.yes'),
      rejectLabel: this.translate.instant('layout.configurations.currency.delete.no'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        if (currency.id) {
          this.currencyService.delete(currency.id).subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('layout.configurations.currency.delete.success.title'),
                detail: this.translate.instant('layout.configurations.currency.delete.success.detail')
              });
              this.loadCurrencies();
              this.loadCenterRateCurrencyOptions();
            },
            error: (error) => {
              console.error('Error deleting currency:', error);
              this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('layout.configurations.currency.delete.error.title'),
                detail: this.translate.instant('layout.configurations.currency.delete.error.detail')
              });
            }
          });
        }
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translate.instant('layout.configurations.currency.delete.cancel.title'),
          detail: this.translate.instant('layout.configurations.currency.delete.cancel.detail'),
          life: 3000
        });
      }
    });
  }

  getStatusLabel(status: CurrencyStatus): string {
    return this.statusLabels[status as keyof typeof CurrencyStatusLabels] || status;
  }

  getSeverity(status: CurrencyStatus): 'success' | 'danger' | 'warning' | 'info' {
    switch (status) {
      case CurrencyStatus.ACTIVE:
        return 'success';
      case CurrencyStatus.INACTIVE:
        return 'danger';
      default:
        return 'info';
    }
  }
}
